import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useWindow } from '@client/window/window.hook';
import {
  SignalCondition,
  SignalConditionInputSchema
} from '@server/data-feed/schemas/input.schema';
import { TradingFormState } from '@client/widgets/trading-form/trading-form.types';
import { getStrategyPath } from '@client/widgets/trading-form/strategy/strategy-config-form.utils';
import { SignalConditionsForm } from '@client/widgets/trading-form/strategy/strategy-config-form.component';
import { useTemplateSave } from '@client/features/trading-templates/template-save.hook';
import { AppButton } from '@client/foundation/design-system/components';
import { AdditionalTradingFormContextProvider } from '@client/widgets/trading-form/trading-form-context';
import type { UiStatePath } from '@client/widgets/trading-form/trading-form-ui-state.hook';
import { TradingMode } from '@shared/types';
import type { TemplateConfigInput } from '@server/trading-templates/schemas/input.schema';
import type {
  DefaultTemplatePolicy,
  FormInitSource
} from '@server/trading-layouts/schemas';
import { templateEditorSurfaceSx } from './template-editor.styles';

export type SignalBlockTemplateEditorWindowProps = {
  templateId?: string;
  initialName?: string;
  initialConditions?: SignalCondition[];
  onSaved?: (savedTemplate: {
    templateId: string;
    name: string;
    config: TemplateConfigInput;
  }) => void;
};

type SignalBlockTemplateEditorProps = SignalBlockTemplateEditorWindowProps & {
  onClose: () => void;
};

const emptyConditions: SignalCondition[] = [];
const strategyPath = getStrategyPath(0);
const signalConditionsPath = `${strategyPath}.value.long.entries` as const;
const initSource: FormInitSource = 'LAYOUT_RESTORE';
const defaultTemplatePolicy: DefaultTemplatePolicy =
  'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES';
const editorMode: TradingMode = 'BACKTESTING';

const buildEditorFormState = (
  conditions: SignalCondition[]
): TradingFormState => ({
  timeframes: [
    {
      timeframe: '1m',
      indicators: [],
      strategy: {
        value: {
          long: {
            entries: conditions,
            exits: []
          }
        }
      }
    }
  ]
});

export const useSignalBlockTemplateEditor = ({
  templateId,
  initialName = '',
  initialConditions = emptyConditions,
  onSaved,
  onClose
}: SignalBlockTemplateEditorProps) => {
  const formApi = useForm<TradingFormState>({
    defaultValues: buildEditorFormState(initialConditions)
  });
  const { reset, getValues } = formApi;
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const isEditMode = Boolean(templateId);
  const [uiStateByPath, setUiStateByPath] = useState<Record<string, unknown>>(
    {}
  );

  useEffect(() => {
    setName(initialName);
    reset(buildEditorFormState(initialConditions));
    setUiStateByPath({});
    setError('');
  }, [initialConditions, initialName, reset, templateId]);

  const setUiState = useCallback(<T,>(path: UiStatePath, value: T) => {
    const key = path.join('.');
    setUiStateByPath((current) => ({
      ...current,
      [key]: value
    }));
  }, []);

  const getUiState = useCallback(
    <T,>(path: UiStatePath, fallback: T): T => {
      const key = path.join('.');
      return (uiStateByPath[key] as T) ?? fallback;
    },
    [uiStateByPath]
  );

  const { saveTemplate, isPending } = useTemplateSave();
  const loading = isPending;

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Template name is required.');
      return;
    }

    try {
      const conditions = z
        .array(SignalConditionInputSchema)
        .parse(getValues(signalConditionsPath) ?? []);
      const config = {
        type: 'signalBlock' as const,
        value: { conditions }
      };

      if (isEditMode && !templateId) {
        setError('Missing template id.');
        return;
      }

      const result = await saveTemplate({
        templateId,
        name: trimmedName,
        isBundle: false,
        config,
        label: 'Signal block template'
      });

      onSaved?.({
        templateId: result.templateId,
        name: trimmedName,
        config: result.config
      });
      onClose();
    } catch (saveError) {
      const message =
        saveError instanceof Error
          ? saveError.message
          : 'Invalid signal block config.';
      setError(message);
    }
  }, [getValues, isEditMode, name, onClose, saveTemplate, templateId]);

  const additionalContextValue = useMemo(
    () => ({
      mode: editorMode,
      initSource,
      defaultTemplatePolicy,
      setUiState,
      getUiState
    }),
    [getUiState, setUiState]
  );

  return {
    formApi,
    isEditMode,
    name,
    error,
    loading,
    setName,
    setError,
    handleSave,
    onClose,
    additionalContextValue
  };
};

export const SignalBlockTemplateEditor: React.FC<
  SignalBlockTemplateEditorWindowProps
> = ({ templateId, initialName, initialConditions, onSaved }) => {
  const { closeWindow } = useWindow();
  const {
    formApi,
    isEditMode,
    name,
    setName,
    setError,
    onClose,
    handleSave,
    error,
    loading,
    additionalContextValue
  } = useSignalBlockTemplateEditor({
    templateId,
    initialName,
    initialConditions,
    onSaved,
    onClose: () => closeWindow('SignalBlockTemplateEditor')
  });

  return (
    <FormProvider {...formApi}>
      <AdditionalTradingFormContextProvider value={additionalContextValue}>
        <Box
          sx={{
            ...templateEditorSurfaceSx,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%',
            minHeight: 0
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isEditMode
              ? 'Edit Signal Block Template'
              : 'Create Signal Block Template'}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Configure reusable signal conditions for a single strategy slot.
          </Typography>

          <TextField
            label="Template name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
            fullWidth
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto'
            }}
          >
            <SignalConditionsForm
              strategyPath={strategyPath}
              direction="long"
              type="entries"
            />
          </Box>

          {error ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block' }}
            >
              {error}
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <AppButton tone="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </AppButton>
            <AppButton tone="primary" onClick={handleSave} disabled={loading}>
              Save
            </AppButton>
          </Box>
        </Box>
      </AdditionalTradingFormContextProvider>
    </FormProvider>
  );
};
